import React from "react";

export default function TermsOfService() {
  return (
    <main style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <p style={styles.badge}>NGC Financial Services / Direct Money</p>
          <h1 style={styles.title}>Terms of Service</h1>
          <h2 style={styles.subtitle}>Conditions Générales d’Utilisation</h2>
          <p style={styles.updated}>
            Last updated / Dernière mise à jour : May 2026
          </p>
        </div>
      </section>

      <section style={styles.container}>
        <div style={styles.card}>
          <LanguageBlock
            lang="English"
            title="Terms of Service"
            sections={englishSections}
          />

          <div style={styles.divider} />

          <LanguageBlock
            lang="Français"
            title="Conditions Générales d’Utilisation"
            sections={frenchSections}
          />
        </div>
      </section>
    </main>
  );
}

function LanguageBlock({ lang, title, sections }) {
  return (
    <div>
      <div style={styles.languageHeader}>
        <span style={styles.langPill}>{lang}</span>
        <h2 style={styles.docTitle}>{title}</h2>
      </div>

      {sections.map((section) => (
        <section key={section.title} style={styles.section}>
          <h3 style={styles.heading}>{section.title}</h3>

          {section.paragraphs?.map((text, index) => (
            <p key={index} style={styles.paragraph}>
              {text}
            </p>
          ))}

          {section.items && (
            <ul style={styles.list}>
              {section.items.map((item, index) => (
                <li key={index} style={styles.listItem}>
                  {item}
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

const englishSections = [
  {
    title: "1. Purpose",
    paragraphs: [
      "These Terms of Service define the conditions for accessing and using the services provided by NGC Financial Services through the Direct Money application and related platforms.",
      "By creating an account or using our services, you fully accept these Terms. If you do not agree with these Terms, you must not use the services.",
    ],
  },
  {
    title: "2. Access to Services",
    paragraphs: [
      "Access to the services is reserved for users with a valid account. You are responsible for maintaining the confidentiality of your login credentials and for all activity carried out through your account.",
      "We reserve the right to suspend, restrict or terminate an account in case of abusive, suspicious, fraudulent or unlawful use.",
    ],
  },
  {
    title: "3. Description of Services",
    paragraphs: [
      "The Direct Money application may allow users to access financial services including:",
    ],
    items: [
      "Sending and receiving money.",
      "Managing an electronic wallet.",
      "Viewing transaction history and account activity.",
      "Accessing additional financial services made available by NGC Financial Services.",
      "The services may be modified, improved, suspended or discontinued at any time depending on operational, technical, commercial or regulatory requirements.",
    ],
  },
  {
    title: "4. User Obligations",
    paragraphs: [
      "You agree to use the services responsibly and in compliance with applicable laws and regulations.",
    ],
    items: [
      "Provide accurate, complete and up-to-date information.",
      "Not use the services for illegal, fraudulent or unauthorized purposes.",
      "Comply with all applicable financial, tax, anti-fraud and anti-money laundering regulations.",
      "Not attempt to compromise, disrupt, reverse engineer, overload or gain unauthorized access to the platform or its systems.",
      "Immediately notify us of any suspected unauthorized access or security breach.",
    ],
  },
  {
    title: "5. Financial Transactions",
    paragraphs: [
      "Transactions made through the application may be subject to verification, fees, exchange rates, limits and compliance checks.",
    ],
    items: [
      "Fees may vary depending on the corridor, payment method, service type, amount, currency and destination country.",
      "Exchange rates are applied at the time of the transaction or quote and may change before confirmation.",
      "Transactions may require compliance validation, identity verification, fraud review or manual approval.",
      "Certain transactions may be delayed, rejected, cancelled or held for security, technical, regulatory or compliance reasons.",
      "You are responsible for ensuring that all recipient, amount, country, currency and payment details are accurate before submitting a transaction.",
    ],
  },
  {
    title: "6. Account Security",
    paragraphs: [
      "You are responsible for protecting your account, password, device and login credentials. Any activity performed through your account may be considered as having been authorized by you.",
      "If you suspect unauthorized access, fraud or compromise of your account, you must contact us immediately.",
    ],
  },
  {
    title: "7. Verification and Compliance",
    paragraphs: [
      "As a financial services provider, we may request identification documents, additional user information or transaction details to comply with Know Your Customer, anti-money laundering, counter-terrorism financing, fraud prevention and regulatory obligations.",
      "Failure to provide required information may result in restricted access, delayed transactions, account suspension or service refusal.",
    ],
  },
  {
    title: "8. Suspension and Termination",
    paragraphs: [
      "We may suspend, restrict or terminate your account or access to the services in the following cases:",
    ],
    items: [
      "Violation of these Terms.",
      "Suspicious, abusive, fraudulent or illegal activity.",
      "Incorrect, incomplete or misleading information.",
      "Security risk or unauthorized access.",
      "Legal, regulatory, compliance or law enforcement requirement.",
    ],
  },
  {
    title: "9. Liability",
    paragraphs: [
      "To the maximum extent permitted by applicable law, NGC Financial Services shall not be held liable for:",
    ],
    items: [
      "Temporary interruptions, maintenance, technical failures or service unavailability.",
      "Errors, delays or losses caused by incorrect information provided by the user.",
      "Losses resulting from unauthorized access caused by user negligence.",
      "Delays, failures or restrictions caused by third-party providers, payment partners, banks, telecom operators or regulatory controls.",
      "Indirect, incidental, consequential or unforeseeable damages.",
    ],
  },
  {
    title: "10. Intellectual Property",
    paragraphs: [
      "All content, trademarks, logos, interfaces, software, technologies, graphics and other materials used in the application are the exclusive property of NGC Financial Services or its licensors.",
      "Any unauthorized reproduction, distribution, modification, reverse engineering or use is strictly prohibited.",
    ],
  },
  {
    title: "11. Privacy and Data Protection",
    paragraphs: [
      "The collection and processing of personal data are governed by our Privacy Policy. By using the services, you acknowledge that you have read and understood our Privacy Policy.",
    ],
  },
  {
    title: "12. Changes to These Terms",
    paragraphs: [
      "We reserve the right to modify these Terms at any time. Users may be informed of significant changes through the application, website or email.",
      "Continued use of the services after changes take effect constitutes acceptance of the updated Terms.",
    ],
  },
  {
    title: "13. Governing Law",
    paragraphs: [
      "These Terms are governed by the laws applicable in the jurisdiction of NGC Financial Services, without prejudice to any mandatory consumer protection rules that may apply in your country of residence.",
    ],
  },
  {
    title: "14. Contact",
    paragraphs: [
      "For questions regarding these Terms, contact us at support@ngcfinancialservices.com.",
    ],
  },
];

const frenchSections = [
  {
    title: "1. Objet",
    paragraphs: [
      "Les présentes Conditions Générales d’Utilisation définissent les modalités d’accès et d’utilisation des services fournis par NGC Financial Services via l’application Direct Money et les plateformes associées.",
      "En créant un compte ou en utilisant nos services, vous acceptez pleinement les présentes conditions. Si vous n’acceptez pas ces conditions, vous ne devez pas utiliser les services.",
    ],
  },
  {
    title: "2. Accès aux services",
    paragraphs: [
      "L’accès aux services est réservé aux utilisateurs disposant d’un compte valide. Vous êtes responsable de la confidentialité de vos identifiants de connexion ainsi que de toute activité effectuée via votre compte.",
      "Nous nous réservons le droit de suspendre, restreindre ou résilier un compte en cas d’utilisation abusive, suspecte, frauduleuse ou illégale.",
    ],
  },
  {
    title: "3. Description des services",
    paragraphs: [
      "L’application Direct Money permet notamment aux utilisateurs d’accéder à des services financiers tels que :",
    ],
    items: [
      "L’envoi et la réception d’argent.",
      "La gestion d’un portefeuille électronique.",
      "La consultation de l’historique des transactions et de l’activité du compte.",
      "L’accès à des services financiers complémentaires proposés par NGC Financial Services.",
      "Les services peuvent évoluer, être améliorés, suspendus ou interrompus à tout moment pour des raisons opérationnelles, techniques, commerciales ou réglementaires.",
    ],
  },
  {
    title: "4. Obligations de l’utilisateur",
    paragraphs: [
      "L’utilisateur s’engage à utiliser les services de manière responsable et conformément aux lois et réglementations applicables.",
    ],
    items: [
      "Fournir des informations exactes, complètes et à jour.",
      "Ne pas utiliser le service à des fins illégales, frauduleuses ou non autorisées.",
      "Respecter les réglementations financières, fiscales, anti-fraude et de lutte contre le blanchiment d’argent applicables.",
      "Ne pas tenter de compromettre, perturber, surcharger, désassembler ou accéder sans autorisation à la plateforme ou à ses systèmes.",
      "Nous informer immédiatement en cas de suspicion d’accès frauduleux ou de faille de sécurité.",
    ],
  },
  {
    title: "5. Transactions financières",
    paragraphs: [
      "Les transactions effectuées via l’application peuvent être soumises à des vérifications, frais, taux de change, limites et contrôles de conformité.",
    ],
    items: [
      "Les frais peuvent varier selon les corridors, moyens de paiement, types de services, montants, devises et pays de destination.",
      "Les taux de change applicables sont ceux indiqués au moment de l’opération ou de la cotation et peuvent évoluer avant confirmation.",
      "Les transactions peuvent faire l’objet d’une validation de conformité, d’une vérification d’identité, d’un contrôle anti-fraude ou d’une approbation manuelle.",
      "Certaines transactions peuvent être différées, refusées, annulées ou retenues pour des raisons de sécurité, techniques, réglementaires ou de conformité.",
      "Vous êtes responsable de l’exactitude des informations relatives au bénéficiaire, au montant, au pays, à la devise et au paiement avant de soumettre une transaction.",
    ],
  },
  {
    title: "6. Sécurité du compte",
    paragraphs: [
      "Vous êtes responsable de la protection de votre compte, de votre mot de passe, de votre appareil et de vos identifiants de connexion. Toute activité effectuée via votre compte peut être considérée comme ayant été autorisée par vous.",
      "En cas de suspicion d’accès non autorisé, de fraude ou de compromission de votre compte, vous devez immédiatement nous contacter.",
    ],
  },
  {
    title: "7. Vérification et conformité",
    paragraphs: [
      "En tant que fournisseur de services financiers, nous pouvons demander des documents d’identification, des informations complémentaires sur l’utilisateur ou des détails relatifs aux transactions afin de respecter nos obligations de connaissance client, de lutte contre le blanchiment d’argent, de lutte contre le financement du terrorisme, de prévention de la fraude et de conformité réglementaire.",
      "Le refus ou l’absence de communication des informations requises peut entraîner une restriction d’accès, un retard de transaction, une suspension du compte ou un refus de service.",
    ],
  },
  {
    title: "8. Suspension et résiliation",
    paragraphs: [
      "Nous pouvons suspendre, restreindre ou résilier votre compte ou votre accès aux services dans les cas suivants :",
    ],
    items: [
      "Violation des présentes conditions.",
      "Activité suspecte, abusive, frauduleuse ou illégale.",
      "Informations incorrectes, incomplètes ou trompeuses.",
      "Risque de sécurité ou accès non autorisé.",
      "Obligation légale, réglementaire, de conformité ou demande d’une autorité compétente.",
    ],
  },
  {
    title: "9. Responsabilité",
    paragraphs: [
      "Dans la limite permise par la loi applicable, NGC Financial Services ne saurait être tenu responsable :",
    ],
    items: [
      "Des interruptions temporaires, opérations de maintenance, défaillances techniques ou indisponibilités du service.",
      "Des erreurs, retards ou pertes liés à des informations incorrectes fournies par l’utilisateur.",
      "Des pertes résultant d’un accès non autorisé dû à une négligence de l’utilisateur.",
      "Des retards, échecs ou restrictions causés par des prestataires tiers, partenaires de paiement, banques, opérateurs télécoms ou contrôles réglementaires.",
      "Des dommages indirects, accessoires, consécutifs ou imprévisibles.",
    ],
  },
  {
    title: "10. Propriété intellectuelle",
    paragraphs: [
      "Tous les contenus, marques, logos, interfaces, logiciels, technologies, éléments graphiques et autres éléments utilisés dans l’application sont la propriété exclusive de NGC Financial Services ou de ses concédants.",
      "Toute reproduction, distribution, modification, ingénierie inverse ou utilisation non autorisée est strictement interdite.",
    ],
  },
  {
    title: "11. Confidentialité et protection des données",
    paragraphs: [
      "La collecte et le traitement des données personnelles sont régis par notre Politique de Confidentialité. En utilisant les services, vous reconnaissez avoir lu et compris notre Politique de Confidentialité.",
    ],
  },
  {
    title: "12. Modifications des conditions",
    paragraphs: [
      "Nous nous réservons le droit de modifier les présentes Conditions Générales d’Utilisation à tout moment. Les utilisateurs pourront être informés des modifications significatives via l’application, le site web ou par e-mail.",
      "La poursuite de l’utilisation des services après l’entrée en vigueur des modifications vaut acceptation des conditions mises à jour.",
    ],
  },
  {
    title: "13. Droit applicable",
    paragraphs: [
      "Les présentes conditions sont régies par les lois applicables dans la juridiction de NGC Financial Services, sans préjudice des règles impératives de protection des consommateurs pouvant s’appliquer dans votre pays de résidence.",
    ],
  },
  {
    title: "14. Contact",
    paragraphs: [
      "Pour toute question relative aux présentes conditions, vous pouvez nous contacter à support@ngcfinancialservices.com.",
    ],
  },
];

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f8fafc",
    color: "#0f172a",
  },
  hero: {
    background: "linear-gradient(135deg, #152556 0%, #223875 100%)",
    color: "#fff",
    padding: "72px 16px 96px",
  },
  heroInner: {
    maxWidth: 980,
    margin: "0 auto",
  },
  badge: {
    display: "inline-block",
    background: "rgba(137, 114, 61, 0.22)",
    color: "#f8e7b0",
    border: "1px solid rgba(248, 231, 176, 0.28)",
    borderRadius: 999,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 18,
  },
  title: {
    fontSize: "clamp(34px, 6vw, 56px)",
    lineHeight: 1.05,
    margin: 0,
    fontWeight: 900,
    letterSpacing: "-0.04em",
  },
  subtitle: {
    fontSize: "clamp(22px, 4vw, 34px)",
    margin: "10px 0 0",
    color: "rgba(255,255,255,0.82)",
    fontWeight: 700,
  },
  updated: {
    marginTop: 18,
    color: "rgba(255,255,255,0.72)",
    fontWeight: 600,
  },
  container: {
    maxWidth: 980,
    margin: "-56px auto 0",
    padding: "0 16px 64px",
  },
  card: {
    background: "#fff",
    borderRadius: 28,
    padding: "clamp(24px, 4vw, 44px)",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.12)",
    border: "1px solid rgba(226, 232, 240, 0.9)",
  },
  languageHeader: {
    marginBottom: 28,
  },
  langPill: {
    display: "inline-block",
    background: "#f5f1e8",
    color: "#89723d",
    borderRadius: 999,
    padding: "7px 12px",
    fontSize: 13,
    fontWeight: 800,
    marginBottom: 12,
  },
  docTitle: {
    color: "#152556",
    fontSize: "clamp(28px, 5vw, 40px)",
    margin: 0,
    fontWeight: 900,
    letterSpacing: "-0.03em",
  },
  section: {
    marginTop: 26,
  },
  heading: {
    color: "#152556",
    fontSize: "clamp(19px, 3vw, 24px)",
    marginBottom: 10,
    fontWeight: 850,
  },
  paragraph: {
    color: "#334155",
    fontSize: 16,
    lineHeight: 1.75,
    margin: "0 0 12px",
  },
  list: {
    margin: "10px 0 0",
    paddingLeft: 22,
  },
  listItem: {
    color: "#334155",
    fontSize: 16,
    lineHeight: 1.75,
    marginBottom: 8,
  },
  divider: {
    height: 1,
    background: "#e2e8f0",
    margin: "46px 0",
  },
};